// File: /src/hooks/clients/useClientReports.js

import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { generateReport } from '../../components/GenReports';

const translateStatus = {
	pending: 'PENDENTE',
	in_progress: 'EM ANDAMENTO',
	completed: 'CONCLUÍDO',
	paid: 'PAGA',
	overdue: 'ATRASADA',
};

const formatCurrency = (value) =>
	Number(value).toLocaleString('pt-BR', {
		style: 'currency',
		currency: 'BRL',
	});

const formatDate = (date) =>
	date ? new Date(date).toLocaleDateString('pt-BR') : '-';

export const useClientReports = ({
	clients = [],
	services = [],
	invoices = [],
}) => {
	const buildSelectedData = useCallback(
		({
			selectedClientsIds = [],
			selectedProjectIds = [],
			selectedInvoiceIds = [],
		}) => {
			const data = {};

			if (selectedClientsIds.length > 0) {
				data.clients = clients.filter((client) =>
					selectedClientsIds.includes(client.id),
				);
			}

			if (selectedProjectIds.length > 0) {
				data.services = services
					.filter((service) =>
						selectedProjectIds.includes(service.id),
					)
					.map((service) => ({
						...service,
						clientName:
							clients.find(
								(client) => client.id === service.clientId,
							)?.name || '-',
					}));
			}

			if (selectedInvoiceIds.length > 0) {
				data.invoices = invoices
					.filter((invoice) =>
						selectedInvoiceIds.includes(invoice.id),
					)
					.map((invoice) => {
						const service = services.find(
							(item) => item.id === invoice.projectId,
						);
						return {
							...invoice,
							serviceName: service?.name || '-',
							clientName:
								clients.find(
									(client) => client.id === service?.clientId,
								)?.name || '-',
						};
					});
			}

			return data;
		},
		[clients, services, invoices],
	);

	const handleGenerateReport = useCallback(
		(reportType, selections) => {
			const selectedData = buildSelectedData(selections);

			if (Object.keys(selectedData).length === 0) {
				toast.error(
					'Selecione pelo menos um item para gerar o relatório.',
				);
				return;
			}

			const config = {
				titles: {
					clients: 'Clientes',
					services: 'Projetos',
					invoices: 'Faturas',
				},
				columns: {
					clients: [
						'ID',
						'Nome',
						'Email',
						'CPF/CNPJ',
						'Telefone',
						'Endereço',
					],
					services: [
						'ID',
						'Projeto',
						'Descrição',
						'Cliente',
						'Valor',
						'Data de Vencimento',
						'Status',
					],
					invoices: [
						'ID',
						'Descrição',
						'Projeto',
						'Cliente',
						'Valor',
						'Data de Vencimento',
						'Status',
					],
				},
				formatters: {
					clients: (client) => [
						client.id,
						client.name || '-',
						client.email || '-',
						client.cpf_cnpj || '-',
						client.phone || '-',
						client.address || '-',
					],
					services: (service) => [
						service.id,
						service.name || '-',
						service.description || '-',
						service.clientName || '-',
						formatCurrency(service.value),
						formatDate(service.due_date),
						translateStatus[service.status] ||
							service.status?.toUpperCase() ||
							'-',
					],
					invoices: (invoice) => [
						invoice.id,
						invoice.description || '-',
						invoice.serviceName || '-',
						invoice.clientName || '-',
						formatCurrency(invoice.value),
						formatDate(invoice.due_date),
						translateStatus[invoice.status] ||
							invoice.status?.toUpperCase() ||
							'-',
					],
				},
			};

			generateReport(reportType, {
				data: selectedData,
				config,
			});
		},
		[buildSelectedData],
	);

	return { handleGenerateReport };
};

export default useClientReports;
